import { inject, injectable, injectFromBase } from 'inversify'
import { isEqual } from 'lodash-es'
import type BaseWrapper from '@/data/models/wrappers/BaseWrapper'
import { SERVICE_TYPES } from '@/core/container/types'
import type LoggerService from '@/services/loggerService'
import TableBaseStorageWrapperService from '@/services/wrappers/table/tableBaseStorageWrapperService'
import type BaseWrapperService from '@/services/wrappers/baseWrapperService'
import type QueryStorageService from '@/services/storage/queryStorageService'
import type { WrapperTableParams, WrapperTableParamsToSave } from '@/services/wrappers/table/types'
import type { Optional, ValueOf } from '@/types'

type DefaultsProvider = () => WrapperTableParams

@injectable()
@injectFromBase()
export default class TableQueryWrapperService extends TableBaseStorageWrapperService {
  private defaultsProviderByWrapperId = new Map<string, DefaultsProvider>()

  constructor(
    @inject(SERVICE_TYPES.BaseWrapperService) protected readonly baseWrapperService: BaseWrapperService<BaseWrapper>,
    @inject(SERVICE_TYPES.LoggerService) protected readonly loggerService: LoggerService,
    @inject(SERVICE_TYPES.QueryStorageService) protected readonly storageService: QueryStorageService,
  ) {
    super(baseWrapperService, loggerService, storageService)
  }

  protected extractDataToStorage(
    params: WrapperTableParams,
    _paramsToSave: WrapperTableParamsToSave,
  ): WrapperTableParams {
    return this.filterParams(params)
  }

  protected transformStorage(toStorage: WrapperTableParams, wrapperId: string): WrapperTableParams {
    const defaults = this.defaultsProviderByWrapperId.get(wrapperId)?.()

    return Object.fromEntries(
      Object.entries(toStorage).map(([key, value]) => {
        if (!defaults) {
          return [key, value]
        }
        const rawDefault = defaults[key as keyof WrapperTableParams] as Optional<ValueOf<WrapperTableParams>>
        if (typeof rawDefault === 'undefined') {
          return [key, value]
        }

        return [key, isEqual(value, rawDefault) ? null : value]
      }),
    ) as WrapperTableParams
  }

  public applyDefaults(
    wrapperId: string,
    defaultsProvider: DefaultsProvider,
    paramsToSave: WrapperTableParamsToSave,
  ): void {
    this.defaultsProviderByWrapperId.set(wrapperId, defaultsProvider)

    const fromUrl = this.getStorageParams(wrapperId)
    const baseStorage = this.extractDataToStorage(fromUrl, paramsToSave)
    const stripped = this.transformStorage(baseStorage, wrapperId)

    this.setStorageParams(wrapperId, stripped, true)
  }

  public destroy(wrapperId: string): void {
    this.defaultsProviderByWrapperId.delete(wrapperId)
    super.destroy(wrapperId)
  }
}
